/**
 * Research & Compare catalogue registry (Phase 2).
 *
 * One source of truth for the six catalogue families that span the whole
 * build journey. Each category either links to an existing mature hub
 * (builders, designs, display homes, packages, products) or to the
 * data-driven directory at /research/:categorySlug.
 *
 * Partner-depth rule (honours "real partners only, no shells"):
 *   >=3 partners in the active state  -> side-by-side compare switches on
 *   1-2 partners                      -> searchable directory + Get matched
 *   0  partners                       -> hidden from the hub entirely
 *
 * Adelaide + Melbourne (SA + VIC) only for now.
 */

// ── Compare field configs, per compare "kind" ──────────────────────────
// Each field: { label, get(item) -> string }. Used by the compare table.
const list = (arr, n = 4) => (Array.isArray(arr) ? arr.slice(0, n) : []);
const pillLabels = (p) => list(p.fact_pills).map((f) => (typeof f === 'string' ? f : f.label)).filter(Boolean);

const TIER_LABEL = { featured: 'Featured partner', partner: 'Premium partner', listed: 'Verified partner' };

export const COMPARE_FIELDS = {
  builder: [
    { label: 'Builder type', get: (p) => p.builderType || '—' },
    { label: 'Price range', get: (p) => p.priceRange || '—' },
    { label: 'Established', get: (p) => p.established || '—' },
    { label: 'Best for', get: (p) => (Array.isArray(p.bestFor) && p.bestFor.length ? p.bestFor.slice(0, 4).join(' · ') : '—') },
    { label: 'Coverage', get: (p) => (Array.isArray(p.states) && p.states.length ? p.states.join(', ') : (p.headquarters || '—')) },
  ],
  partner: [
    { label: 'Partner status', get: (p) => TIER_LABEL[(p.tier || 'listed')] || 'Verified partner' },
    { label: 'Specialises in', get: (p) => pillLabels(p).join(' · ') || '—' },
    { label: 'Years established', get: (p) => (p.years_established ? `${p.years_established} years` : '—') },
    { label: 'Areas served', get: (p) => (p.service_states || []).filter((s) => ['SA', 'VIC'].includes(s)).join(', ') || '—' },
  ],
  hl_package: [
    { label: 'Bedrooms', get: (p) => (p.bedrooms != null ? String(p.bedrooms) : '—') },
    { label: 'Bathrooms', get: (p) => (p.bathrooms != null ? String(p.bathrooms) : '—') },
    { label: 'Car spaces', get: (p) => (p.garages != null ? String(p.garages) : '—') },
    { label: 'Home size', get: (p) => (p.floor_area ? `${Math.round(parseFloat(p.floor_area))} m²` : '—') },
    { label: 'Land size', get: (p) => (p.area_m2 ? `${Math.round(p.area_m2)} m²` : '—') },
    { label: 'Storeys', get: (p) => (p.is_double_storey ? 'Double storey' : 'Single storey') },
    { label: 'Frontage', get: (p) => (p.frontage_m ? `${Math.round(p.frontage_m)} m` : '—') },
    { label: 'Inclusions listed', get: (p) => (Array.isArray(p.inclusions) ? String(p.inclusions.length) : '—') },
    { label: 'Suburb', get: (p) => `${p.suburb || '—'}${p.state ? `, ${p.state}` : ''}` },
  ],
  display_home: [
    { label: 'Suburb', get: (p) => p.suburb || '—' },
    { label: 'Address', get: (p) => p.address || '—' },
    { label: 'State', get: (p) => p.state || '—' },
    { label: 'Opening hours', get: (p) => p.hours || p.opening_hours || 'See builder site' },
  ],
  product: [
    { label: 'Price', get: (p) => (p.price != null ? `$${p.price}` : '—') },
    { label: 'Was', get: (p) => (p.list_price != null && p.list_price !== p.price ? `$${p.list_price}` : '—') },
    { label: 'You save', get: (p) => (p.save ? `$${p.save}` : '—') },
    { label: 'Supplier', get: (p) => (p.supplier?.name || '—') },
    { label: 'Type', get: (p) => p.product_kind || p.kind || '—' },
  ],
};

export const KIND_LABEL = {
  builder: 'builders',
  partner: 'partners',
  hl_package: 'house & land packages',
  display_home: 'display homes',
  product: 'products',
};

// ── Directory category match predicate ─────────────────────────────────
export const matchesCategory = (partner, cat) => {
  if (!partner || !cat?.match) return false;
  const c = (partner.category || '').toLowerCase();
  const sub = (partner.subcategory || '').toLowerCase();
  if (cat.match.category && c !== cat.match.category) return false;
  if (cat.match.subcategories && !cat.match.subcategories.includes(sub)) return false;
  return true;
};

export const inState = (partner, state) =>
  Array.isArray(partner?.service_states) && partner.service_states.includes(state);

// ── The six families ───────────────────────────────────────────────────
// category fields:
//   slug, label, blurb, icon(lucide name), kind
//   existingRoute: { SA, VIC } or string  -> links to a mature hub
//   compareRoute: string                  -> existing compare experience
//   directory: true                       -> rendered by /research/:slug
//   match: { category, subcategories? }    -> directory partner filter
export const FAMILIES = [
  {
    key: 'land',
    label: 'Land & location',
    icon: 'MapPin',
    blurb: 'Where you build — land, suburbs and ready-to-go packages.',
    categories: [
      { slug: 'house-and-land', label: 'House & land packages', icon: 'Home', kind: 'hl_package',
        blurb: 'Bundled land + build, compared on beds, size, inclusions and frontage.',
        existingRoute: { SA: '/house-and-land-adelaide', VIC: '/house-and-land-melbourne' }, catalogueCompare: true },
      { slug: 'suburbs', label: 'Suburbs & growth areas', icon: 'Building2',
        blurb: 'Research where to build across Adelaide and Melbourne.',
        existingRoute: { SA: '/suburbs', VIC: '/suburbs' } },
    ],
  },
  {
    key: 'design',
    label: 'Design',
    icon: 'PencilRuler',
    blurb: 'What you build — floor plans and display homes to walk through.',
    categories: [
      { slug: 'home-designs', label: 'Home designs & floor plans', icon: 'LayoutGrid',
        blurb: 'Filter by builder, storeys and bedrooms, then compare up to 3 plans side-by-side.',
        existingRoute: { SA: '/designs', VIC: '/designs' }, compareRoute: '/home-designs/compare' },
      { slug: 'display-homes', label: 'Display homes', icon: 'DoorOpen', kind: 'display_home',
        blurb: 'Real display homes you can visit, with addresses and opening details.',
        existingRoute: { SA: '/display-homes-adelaide', VIC: '/melbourne-display-home-directory' }, catalogueCompare: true },
    ],
  },
  {
    key: 'build',
    label: 'Who builds it',
    icon: 'Hammer',
    blurb: 'The builders and specialist trades who deliver the work.',
    categories: [
      { slug: 'home-builders', label: 'Home builders', icon: 'Building2',
        blurb: 'Compare partner new-home builders on type, price band and service area.',
        existingRoute: { SA: '/builders', VIC: '/builders' }, compareRoute: '/compare',
        match: { category: 'professional', subcategories: ['builder'] } },
      { slug: 'renovation-extension-builders', label: 'Renovation & extension builders', icon: 'Hammer', kind: 'partner',
        blurb: 'Specialists for renovations, extensions and home improvements.',
        directory: true, match: { category: 'professional', subcategories: ['renovation_builder', 'extension_builder', 'home_improvement_builder'] } },
    ],
  },
  {
    key: 'advise',
    label: 'Advisers & certifiers',
    icon: 'PencilRuler',
    blurb: 'The professionals who design, document and sign off your build.',
    categories: [
      { slug: 'architects', label: 'Architects', icon: 'PencilRuler', kind: 'partner',
        blurb: 'Registered architects for bespoke and high-end residential design.',
        directory: true, match: { category: 'professional', subcategories: ['architect'] } },
      { slug: 'building-designers', label: 'Building designers', icon: 'PencilRuler', kind: 'partner',
        blurb: 'Building designers and draftspeople for plans and documentation.',
        directory: true, match: { category: 'professional', subcategories: ['building_designer'] } },
      { slug: 'building-certifiers', label: 'Building certifiers', icon: 'FileCheck', kind: 'partner',
        blurb: 'Private certifiers who assess and approve your building work.',
        directory: true, match: { category: 'professional', subcategories: ['private_certifier'] } },
      { slug: 'building-inspectors', label: 'Building inspectors', icon: 'ShieldCheck', kind: 'partner',
        blurb: 'Independent inspectors for stage, handover and defect inspections.',
        directory: true, match: { category: 'professional', subcategories: ['building_inspector', 'house_inspect'] } },
    ],
  },
  {
    key: 'finance',
    label: 'Finance & legal',
    icon: 'Landmark',
    blurb: 'The money and legal partners that get the deal done.',
    categories: [
      { slug: 'finance-brokers', label: 'Finance & mortgage brokers', icon: 'Landmark', kind: 'partner',
        blurb: 'Brokers who structure construction and home loans.',
        directory: true, match: { category: 'professional', subcategories: ['mortgage_broker', 'broker', 'finance_broker', 'finance'] } },
      { slug: 'conveyancers', label: 'Conveyancers & lawyers', icon: 'Scale', kind: 'partner',
        blurb: 'Conveyancers and property lawyers for contracts and settlement.',
        directory: true, match: { category: 'professional', subcategories: ['conveyancer', 'property_lawyer'] } },
    ],
  },
  {
    key: 'products',
    label: 'Products & materials',
    icon: 'Package',
    blurb: 'What goes in it — suppliers, materials and shop-ready products.',
    categories: [
      { slug: 'shop-products', label: 'Shop products', icon: 'Package', kind: 'product',
        blurb: 'Browse partner products for fit-out, energy and the finishing touches.',
        existingRoute: { SA: '/products', VIC: '/products' } },
      { slug: 'suppliers', label: 'Suppliers & materials', icon: 'Store', kind: 'partner',
        blurb: 'Benchtops, bathroom, tiles, solar, tools and building materials from partner suppliers.',
        directory: true, match: { category: 'supplier' } },
    ],
  },
];

// Flat lookup by directory slug.
export const CATEGORY_BY_SLUG = FAMILIES.reduce((acc, fam) => {
  fam.categories.forEach((c) => { acc[c.slug] = { ...c, familyLabel: fam.label }; });
  return acc;
}, {});

export const COMPARE_MIN = 3; // partners needed in-state before compare unlocks
